import {
    type AuthSettings,
    ClassAccess,
    ClassJoinPermission,
    type DefaultSettings,
    type FeatureSettings,
    InstructorTARestrictedAccess,
    ParticipationSortType,
    type SchoolLevelOptions,
    ScreenShareResolutions,
    UnauthUserAccess,
    WebExpOptions
} from '../types/adminConfig';
import { type SchoolSettings } from '../types/apiTypes';
import { ConfigIngestModes, Permission } from '../types/recordingTypes';

export const SETTINGS_API = '/api/admin/settings';

export const DEFAULT_SCHOOL_OPTIONS = Object.freeze<SchoolLevelOptions>({
    webExperience: WebExpOptions.NextGen,
    unauthUserAccess: UnauthUserAccess.Allow,
    unauthUserText: null,
    allowVanillaZoom: false,
    forceWebAsChromebook: false,
    azureTenantIds: [],
    zoomAccountNumbers: [],
    updateMicrosoftLicenseDays: 0, // Falsy indicates never

    // userAccountCreation: Default depends on if Teams
    userAccountCreationDomains: [],
    salesTrainingMode: false,
    classJoinPermission: ClassJoinPermission.All,
    rosterSelfEnrollment: false,
    allowTabLocking: true,
    autoProvisionNewUsersZoom: true,
    autoProvisionNewUsersMicrosoft: false,
    disableTeamsRecording: false,
    hasAiTa: false,
    subClasses: false,
    hasFileAnnotations: false,
    hasPresentations: true,
    hasNextGenPolls: true,
    attendanceGradeTardyScore: 80,
    allowNextGenMobile: true,
    enablePrivateDiscussion: false,
    orgScreenshareRes: ScreenShareResolutions.High,
    hasNextGenFiles: true,
    hasNextGenGoogleDrive: false,
    hasNextGenOneDrive: false,
    hasTabScreenshare: true,
    hasTabScreenshareShareAudio: true,
    hasTabScreenshareAnnotation: true,
    enableNextGenFilesGoogleServiceAccount: false,
    allowVirtualBackgroundInModeratePerfMode: false,
    hasTelephony: false,
    hasAllBreakoutTranscripts: true,
    hasEndUserSurvey: InstructorTARestrictedAccess.Everyone,
    hasEndUserPerformanceTest: true,
    allowCustomVirtualBackgroundUpload: true,
    disableCameraMicrophoneWhenRecording: false,
    hasNextGenSidebar: true,
    enableACSOptimalVideoCount: false,
    enableLearnerEngagement: false,
    recordingsDisablePublicOption: false,
    recordingsDisableDownloads: false,
    recordingsExcludeStudentsActiveSpeaker: false,
    recordingsRestrictChatParticipants: false,
    participationSort: ParticipationSortType.Engagement,
    hasInMeetingTechCheck: true,
    hasNextGenChat: true,
    hasWhiteboardBORType: true,
    permittedEmbedDomains: ['menti.com', 'sciencegeek.net', 'docebosaas.com'],
    excludedEmbedDomains: ['moravian.instructure.com', 'canvas.moravian.edu'],
    hasSSOFeature: false,
    hasClassAuth: true,
    hasMsftAuth: true,
    hasScheduleLinkOnClassLanding: true,
    useIdentifierInRoster: false,
    disableClassBackgrounds: false,
    allowLmsGroupBreakouts: true,
    hasNextGenChatActivityFeed: true,
    hasNextGenLearningToolSidebar: true,
    hasSyncSeatingCharts: true,
    allowInstructorsTranscriptDownload: false,
    hasBreakoutRoomCrossCommunication: false,
    showAdvancedFollowMeOptions: false,
    autoJoinWhenSessionActive: false,
    allowInstructorAutoJoinPriorMinutes: 0,
    allowStudentJoinPriorMinutes: 0,
    classInvitationEmailAddressOverride: ''
});

const COMMON_FEATURE_SETTINGS = Object.freeze<
    Omit<FeatureSettings, 'classGradebook' | 'promptTeachersForFeedback' | 'promptStudentsForFeedback'>
>({
    classDocument: true,
    classAssessments: true,
    lms: true,
    hasPanicMode: false,
    enableLogAutoUpload: true,
    surveys: true,
    polling: true,
    proctoring: true,
    whiteboard: true,
    assignments: true,
    chat: true,
    loseFocusTracking: true,
    webApp: false,
    hasShareFiles: true,
    allowApp: false,
    allowZoom: false,
    autoTranscriptions: true,
    classNotes: true,
    sendInviteEmails: true,
    showMeetingInfo: true,
    notifyOutdatedClassVersion: false,
    enforceClassMinimumVersion: true,
    recordingLegacyZoomList: false,
    forceWebAppChromebooks: false,
    recordingInitialPermission: Permission.ADMINS,
    recordingIngestMode: ConfigIngestModes.COPY,
    classAccess: ClassAccess.All,
    recordingParticipantsCanView: false,
    recordingTabAccess: ClassAccess.Lti,
    recordingRestrictGallery: false,
    attendanceTabAccess: ClassAccess.None,
    restrictSessionsRecordingsByGroups: true,
    google: false,
    msOffice: true,
    aiTa: false,
    allowInstructorSwap: false,
    enableBreakoutRoomCloudRecording: true,
    preventLeaveSessionRedirect: false,
    enableBreakoutRoomSecurity: false,
    whiteboardScreenshotFeature: false,
    instructorChatDownload: false,
    isBbData: false,
    dataPortalAdmin: false,
    dataPortalBusinessAnalyst: false,
    dataPortalClassBusinessAnalyst: false,
    dataPortalCustomerSupport: true,
    dataPortalInstructor: false,
    dataPortalStudent: false,
    allowAiForSupport: false,
    allowAiForInsights: false,
    options: DEFAULT_SCHOOL_OPTIONS,
    overrides: {}
});

const DEFAULT_AUTH_SETTINGS = Object.freeze<AuthSettings>({
    vanityUrl: '',
    domains: [],
    joinMeetingButton: true,
    allowPMI: true,
    forcedAuthentication: false,
    autoVerify: 'never',
    unauthUserAccess: UnauthUserAccess.Allow,
    unauthUserText: null
});

export const DEFAULT_ADMIN_SETTINGS = Object.freeze<DefaultSettings>({
    academic: {
        organizationType: 'academic',
        zoomAppId: 'collab',
        terminologySettings: {
            hostTitle: 'instructor',
            assistantsTitle: 'assistant',
            studentsTitle: 'student',
            rosterTitle: 'roster',
            gradebookTitle: 'gradebook',
            documentTitle: 'syllabus',
            proctoringTitle: 'proctoring_view'
        },
        featureSettings: {
            ...COMMON_FEATURE_SETTINGS,
            classGradebook: true,
            promptTeachersForFeedback: true,
            promptStudentsForFeedback: false
        },
        recordingSettings: {
            autoSave: false,
            cloudSave: false,
            localSave: false,
            participantsCanDownload: false
        },
        classForTeamsSettings: {
            updateMicrosoftLicenseDays: 0, // Falsy indicates never
            azureTenantIds: [],
            hasAzurePhoneNumbers: false
        },
        authSettings: {
            ...DEFAULT_AUTH_SETTINGS,
            joinMeetingButton: false
        }
    },
    corporate: {
        organizationType: 'corporate',
        zoomAppId: 'collab',
        terminologySettings: {
            hostTitle: 'host',
            assistantsTitle: 'co_host',
            studentsTitle: 'participant',
            rosterTitle: 'roster',
            gradebookTitle: 'results',
            documentTitle: 'outline',
            proctoringTitle: 'collaboration_view'
        },
        featureSettings: {
            ...COMMON_FEATURE_SETTINGS,
            classGradebook: false,
            promptTeachersForFeedback: false,
            promptStudentsForFeedback: true
        },
        recordingSettings: {
            autoSave: false,
            cloudSave: false,
            localSave: false,
            participantsCanDownload: false
        },
        classForTeamsSettings: {
            updateMicrosoftLicenseDays: 0, // Falsy indicates never
            azureTenantIds: [],
            hasAzurePhoneNumbers: false
        },
        authSettings: {
            ...DEFAULT_AUTH_SETTINGS,
            joinMeetingButton: true
        }
    }
});

export const DEFAULT_SCHOOL_SETTINGS = Object.freeze<SchoolSettings>({
    assistants_title: 'assistant',
    document_title: 'syllabus',
    gradebook_title: 'gradebook',
    host_title: 'instructor',
    organization_type: 'academic',
    roster_title: 'roster',
    students_title: 'student',
    proctoring_title: 'proctoring_view',
    vanity_url: '',
    domains: '',
    forced_authentication: false,
    force_web_app_chromebooks: true,
    allow_pmi: true,
    enable_log_auto_upload: true,
    has_assessments: true,
    has_assignments: true,
    has_chat: true,
    has_document: true,
    has_gradebook: true,
    has_lms: true,
    has_lose_focus_tracking: true,
    has_panic_mode: false,
    has_surveys: true,
    has_polling: false,
    has_proctoring: true,
    has_share_files: true,
    has_web_app: true,
    has_app: false,
    has_zoom: false,
    zoom_app_id: 'user',
    has_whiteboard: true,
    join_class_by_meeting_id: true,
    auto_transcriptions: false,
    class_notes: false,
    send_invite_emails: true,
    show_meeting_info: true,
    recording_auto_save: false,
    recording_cloud_save: false,
    recording_local_save: false,
    recording_interactive_player: true,
    recording_initial_permission: Permission.ROSTER,
    recording_ingest_mode: ConfigIngestModes.TRACK,
    recording_participants_can_view: false,
    recording_participants_can_download: false,
    recording_restrict_gallery: false,
    recording_tab_access: ClassAccess.All,
    attendance_tab_access: ClassAccess.All,
    restrict_sessions_recordings_by_groups: true,
    prompt_teachers_for_feedback: true,
    prompt_students_for_feedback: false,
    notify_outdated_class_version: false,
    enforce_class_minimum_version: true,
    auto_verify: 'never',
    has_google: false,
    has_ms_office: true,
    has_ai_ta: false,
    class_access: ClassAccess.All,
    allow_instructor_swap: false,
    enable_breakout_room_cloud_recording: true,
    prevent_leave_session_redirect: false,
    enable_breakout_room_security: false,
    whiteboard_screenshot_feature: false,
    instructor_chat_download: false,
    is_bb_data: false,
    data_portal_admin: false,
    data_portal_business_analyst: false,
    data_portal_class_business_analyst: false,
    data_portal_customer_support: true,
    data_portal_instructor: false,
    data_portal_student: false,
    allow_ai_for_support: false,
    allow_ai_for_insights: false,
    azure_tenant_id: null,
    has_azure_phone_numbers: false,
    azure_client_id: undefined,
    options: DEFAULT_SCHOOL_OPTIONS,
    overrides: {}
});
